/* eslint-disable react-refresh/only-export-components -- compound component (AppShell.Header/Sidebar/Content) собирается через Object.assign */
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Menu, PanelLeftClose, PanelLeftOpen, X } from 'lucide-react';
import { useUiStore } from '@/store/uiStore';
import { useTranslation } from '@/i18n';
import { cx } from '@/components/ui/utils';
import './AppShell.css';

export interface AppShellNavItem {
  path: string;
  label: string;
  icon: ReactNode;
  active?: boolean;
  separatorBefore?: boolean;
  /** Приглушённый стиль (напр. ссылка на админку в футере). */
  muted?: boolean;
}

interface AppShellRootProps {
  children: ReactNode;
}

function AppShellRoot({ children }: AppShellRootProps) {
  const sidebarCollapsed = useUiStore((s) => s.sidebarCollapsed);
  const mobileDrawerOpen = useUiStore((s) => s.mobileDrawerOpen);
  const toggleMobileDrawer = useUiStore((s) => s.toggleMobileDrawer);
  const closeMobileDrawer = useUiStore((s) => s.closeMobileDrawer);
  const t = useTranslation();

  // Esc закрывает мобильный drawer.
  useEffect(() => {
    if (!mobileDrawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMobileDrawer();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileDrawerOpen, closeMobileDrawer]);

  return (
    <div
      className={cx(
        'app-shell',
        sidebarCollapsed && 'app-shell--rail',
        mobileDrawerOpen && 'app-shell--drawer-open',
      )}
    >
      {children}
      <button
        type="button"
        className="app-shell__mobile-menu"
        onClick={toggleMobileDrawer}
        aria-label={mobileDrawerOpen
          ? t('sidebar.closeMenu', {}, 'Close menu')
          : t('sidebar.openMenu', {}, 'Open menu')}
      >
        {mobileDrawerOpen ? (
          <X size={22} aria-hidden="true" />
        ) : (
          <Menu size={22} aria-hidden="true" />
        )}
      </button>
      <button
        type="button"
        className="app-shell__backdrop"
        aria-label={t('sidebar.closeMenu', {}, 'Close menu')}
        tabIndex={mobileDrawerOpen ? 0 : -1}
        onClick={closeMobileDrawer}
      />
    </div>
  );
}

interface AppShellHeaderProps {
  brand: ReactNode;
  actions?: ReactNode;
}

function AppShellHeader({ brand, actions }: AppShellHeaderProps) {
  const toggleMobileDrawer = useUiStore((s) => s.toggleMobileDrawer);
  const t = useTranslation();

  return (
    <header className="app-shell__header">
      <div className="app-shell__header-inner">
        <div className="app-shell__header-left">
          <button
            type="button"
            className="app-shell__burger"
            onClick={toggleMobileDrawer}
            aria-label={t('sidebar.openMenu', {}, 'Open menu')}
          >
            <Menu size={22} aria-hidden="true" />
          </button>
          {brand}
        </div>
        {actions ? <div className="app-shell__header-actions">{actions}</div> : null}
      </div>
    </header>
  );
}

interface AppShellSidebarProps {
  items: AppShellNavItem[];
  footerItems?: AppShellNavItem[];
  brand?: ReactNode;
  railBrand?: ReactNode;
  userMenu?: ReactNode;
  railUserMenu?: ReactNode;
}

function AppShellSidebar({
  items,
  footerItems = [],
  brand,
  railBrand,
  userMenu,
  railUserMenu,
}: AppShellSidebarProps) {
  const sidebarCollapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const closeMobileDrawer = useUiStore((s) => s.closeMobileDrawer);
  const t = useTranslation();
  const toggleTitle = sidebarCollapsed
    ? t('sidebar.expand', {}, 'Expand sidebar')
    : t('sidebar.collapse', {}, 'Collapse sidebar');

  const renderItem = (item: AppShellNavItem) => (
    <div key={item.path} className="app-shell__nav-entry">
      {item.separatorBefore && <div className="app-shell__nav-separator" />}
      <Link
        to={item.path}
        onClick={closeMobileDrawer}
        title={sidebarCollapsed ? item.label : undefined}
        aria-label={sidebarCollapsed ? item.label : undefined}
        className={cx(
          'app-shell__nav-item',
          item.active && 'is-active',
          item.muted && 'app-shell__nav-item--muted',
        )}
      >
        <span className="app-shell__nav-icon" aria-hidden="true">
          {item.icon}
        </span>
        <span className="app-shell__nav-label">{item.label}</span>
      </Link>
    </div>
  );

  return (
    <aside className="app-shell__sidebar" aria-label={t('navigation.main', {}, 'Main navigation')}>
      {(brand || railBrand) && (
        <div className="app-shell__sidebar-brand">
          {brand ? <div className="app-shell__brand-expanded">{brand}</div> : null}
          <div className="app-shell__brand-collapsed">{railBrand ?? brand}</div>
        </div>
      )}
      <nav className="app-shell__nav">{items.map(renderItem)}</nav>
      <div className="app-shell__sidebar-footer">
        {footerItems.map(renderItem)}
        {userMenu ? <div className="app-shell__sidebar-user app-shell__sidebar-user--expanded">{userMenu}</div> : null}
        {railUserMenu ? <div className="app-shell__sidebar-user app-shell__sidebar-user--collapsed">{railUserMenu}</div> : null}
        {(footerItems.length > 0 || userMenu || railUserMenu) && <div className="app-shell__sidebar-footer-separator" />}
        <button
          type="button"
          className="app-shell__rail-toggle"
          onClick={toggleSidebar}
          title={toggleTitle}
          aria-label={toggleTitle}
        >
          {sidebarCollapsed ? (
            <PanelLeftOpen size={18} aria-hidden="true" />
          ) : (
            <PanelLeftClose size={18} aria-hidden="true" />
          )}
          <span className="app-shell__rail-toggle-label">{toggleTitle}</span>
        </button>
      </div>
    </aside>
  );
}

interface AppShellContentProps {
  children: ReactNode;
}

function AppShellContent({ children }: AppShellContentProps) {
  return (
    <main className="app-shell__content">
      <div className="app-shell__container">{children}</div>
    </main>
  );
}

export const AppShell = Object.assign(AppShellRoot, {
  Header: AppShellHeader,
  Sidebar: AppShellSidebar,
  Content: AppShellContent,
});
